import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { TPageProps } from 'models/common.model'
import { useSelector } from 'react-redux'
import { selectUUID } from 'root-redux/selects/user'
import { useDelayedContinue } from 'hooks/useDelayedContinue'
import { useNextStep } from 'hooks/useNextStep'
import { Option, OptionType } from 'components/Option'
import { googleAnalyticsLogger } from 'services/googleAnalytics.service'
import { useStartSession } from 'hooks/useStartSession'
import { Container } from 'components/Container'
import { AnswerButtonLight } from 'components/AnswerButtonLight'
import { StyledSelectGoal as S } from './SelectGoal.styles'
import { OPTION_VALUES, QUESTION } from './constants'

export const SelectGoal: React.FC<TPageProps> = ({ pageId, nextPagePath }) => {
  const { t } = useTranslation()
  const uuid = useSelector(selectUUID)
  const { isAnswersDisabled, handleChange, setIsAnswersDisabled } =
    useDelayedContinue()

  const handleContinue = useNextStep({
    pageId,
    question: QUESTION,
    nextPagePath,
  })

  const optionProps = useMemo(
    () => ({
      name: pageId,
      type: OptionType.RADIO,
      onChange: (value: string) => {
        handleChange(value, handleContinue)
        setIsAnswersDisabled(true)

        window.fbq('track', 'CustomizeProduct', {}, { eventID: uuid })
        googleAnalyticsLogger.logFirstPageCompleted()
      },
    }),
    [handleChange, setIsAnswersDisabled, handleContinue, pageId, uuid],
  )

  useStartSession()

  return (
    <Container>
      <S.Wrapper>
        <S.Subtitle>
          <Trans
            i18nKey="onboarding.selectGoal.subtitle"
            components={[<br />]}
          />
        </S.Subtitle>
        <S.Title>{t('onboarding.selectGoal.title')}</S.Title>
        <S.SelectGoal>{t('onboarding.selectGoal.chooseOption')}</S.SelectGoal>

        <Option
          {...optionProps}
          disabled={isAnswersDisabled}
          value={OPTION_VALUES.BOOST_MOOD}
        >
          <AnswerButtonLight>
            {t('onboarding.selectGoal.options.boostMood')}
          </AnswerButtonLight>
        </Option>

        <Option
          {...optionProps}
          disabled={isAnswersDisabled}
          value={OPTION_VALUES.RELIEVE_STRESS_AND_ANXIETY}
        >
          <AnswerButtonLight>
            {t('onboarding.selectGoal.options.relieveStressAndAnxiety')}
          </AnswerButtonLight>
        </Option>

        <Option
          {...optionProps}
          disabled={isAnswersDisabled}
          value={OPTION_VALUES.GET_RID_OF_BRAIN_FOG}
        >
          <AnswerButtonLight>
            {t('onboarding.selectGoal.options.getRidOfBrainFog')}
          </AnswerButtonLight>
        </Option>

        <Option
          {...optionProps}
          disabled={isAnswersDisabled}
          value={OPTION_VALUES.ALL_THAT_WAS_MENTIONED}
        >
          <AnswerButtonLight>
            {t('onboarding.selectGoal.options.allThatWasMentioned')}
          </AnswerButtonLight>
        </Option>
      </S.Wrapper>
    </Container>
  )
}
